import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Crown,
  CheckCircle2,
  Loader2,
  Mail,
  Calendar,
  BookOpen,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CollectiveWelcomePage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError('No checkout session found.');
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/membership/checkout/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (!data.is_active && tries < 8) {
          tries += 1;
          setTimeout(poll, 1500);
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || 'Could not verify membership');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (error) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 bg-background">
        <Card className="max-w-md w-full p-8 bg-card border-silver/20 text-center">
          <p className="text-foreground mb-4">{error}</p>
          <Link to="/collective"><Button variant="outline">Back to Collective</Button></Link>
        </Card>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 bg-background gap-3">
        <Loader2 className="w-8 h-8 text-secondary animate-spin" />
        <p className="text-sm text-muted-foreground">Confirming your membership…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="collective-welcome-root">
      <section className="border-b border-silver/15">
        <div className="container mx-auto px-4 py-16 max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-secondary/20 flex items-center justify-center">
              <Crown className="w-8 h-8 text-secondary" />
            </div>
            <p className="text-xs uppercase tracking-widest text-secondary font-semibold mb-3">Welcome home</p>
            <h1 className="font-heading text-4xl md:text-5xl text-foreground mb-4 leading-tight">
              You&apos;re in The Collective.
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed mb-6">
              I see you. Take a breath — you just made a real choice. The kind that bends a timeline.
            </p>
            {status.is_active ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm font-medium">
                <CheckCircle2 className="w-4 h-4" /> Active member · {status.customer_email}
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm font-medium">
                <Loader2 className="w-4 h-4 animate-spin" /> Membership activating… (this can take a moment)
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <section>
        <div className="container mx-auto px-4 py-12 max-w-3xl">
          <h2 className="font-heading text-2xl text-foreground mb-6 text-center">What happens next</h2>
          <div className="space-y-3">
            {[
              { icon: Mail, title: 'Check your inbox', detail: 'I just sent you a welcome email with your member portal access and the schedule for this week&apos;s live sessions.' },
              { icon: BookOpen, title: 'The E-Book is yours', detail: 'Download from the member portal — and start the Daily Power Practice tonight if you can.' },
              { icon: Calendar, title: 'See you live this week', detail: 'The Weekly Power Hour Recode happens every week. Your seat is reserved.' },
            ].map((step) => {
              const Icon = step.icon;
              return (
                <Card key={step.title} className="p-5 bg-card border-silver/20 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary/15 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-foreground mb-1">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: step.detail }} />
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/circle">
              <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid="enter-portal-button">
                Enter The Collective Portal →
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground mt-3">
              (Portal launching this week — your access link is in your welcome email.)
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
